// src/features/dashboard/components/ManagerDashboard/ProductivityReport/WtdTrendChart.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

import {
  ProductivityTrendResponse,
} from '../../../types/Manager/productivityReport.types';

// ==========================================================================
// PROPS
// ==========================================================================

interface Props {
  data?: ProductivityTrendResponse[];
}

// ==========================================================================
// CHART DATA
// ==========================================================================

interface ChartDataItem {
  axisKey: string;
  workDate: string;
  dayName: string;
  day: string;
  dateLabel: string;
  current: number;
  previous: number;
}

// ==========================================================================
// TOOLTIP
// ==========================================================================

interface CustomTooltipProps {
  active?: boolean;

  payload?: Array<{
    dataKey?: string;
    value?: number | string;
    payload?: ChartDataItem;
  }>;

  label?: string;
}

// ==========================================================================
// CONSTANTS
// ==========================================================================

const CURRENT_COLOR = '#284F79';
const PREVIOUS_COLOR = '#95A5B1';
const GRID_COLOR = '#ECEFF3';
const AXIS_COLOR = '#344054';

const MIN_VISIBLE_POINTS = 3;

// ==========================================================================
// DATE HELPERS
// ==========================================================================

const getDatePart = (
  workDate: string,
): string => {
  if (!workDate) {
    return '';
  }

  return String(workDate)
    .split('T')[0]
    .trim();
};

// ==========================================================================

const parseDate = (
  workDate: string,
): Date | null => {
  const datePart =
    getDatePart(workDate);

  if (!datePart) {
    return null;
  }

  const parts =
    datePart.split('-');

  if (parts.length !== 3) {
    return null;
  }

  const year =
    Number(parts[0]);

  const month =
    Number(parts[1]);

  const day =
    Number(parts[2]);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return null;
  }

  const date =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day,
      ),
    );

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return null;
  }

  return date;
};

// ==========================================================================

const getDateNumber = (
  workDate: string,
): string => {
  const datePart =
    getDatePart(workDate);

  if (!datePart) {
    return '';
  }

  const parts =
    datePart.split('-');

  if (parts.length !== 3) {
    return '';
  }

  return parts[2] || '';
};

// ==========================================================================

const getShortDayName = (
  dayName: string,
): string => {
  return String(dayName || '')
    .trim()
    .substring(0, 3);
};

// ==========================================================================

const getShortDateLabel = (
  dayName: string,
  workDate: string,
): string => {
  const shortDay =
    getShortDayName(dayName);

  const dateNumber =
    getDateNumber(workDate);

  if (
    !shortDay &&
    !dateNumber
  ) {
    return '';
  }

  if (!shortDay) {
    return dateNumber;
  }

  if (!dateNumber) {
    return shortDay;
  }

  return `${shortDay} ${dateNumber}`;
};

// ==========================================================================

const getLongDateLabel = (
  workDate: string,
): string => {
  const date =
    parseDate(workDate);

  if (!date) {
    return getDatePart(
      workDate,
    );
  }

  return date.toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    },
  );
};

// ==========================================================================
// TOOLTIP
// ==========================================================================

const CustomTooltip = ({
  active,
  payload,
  label,
}: CustomTooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  const current =
    payload.find(
      (item) =>
        item.dataKey ===
        'current',
    );

  const previous =
    payload.find(
      (item) =>
        item.dataKey ===
        'previous',
    );

  const currentValue =
    current?.value !== undefined &&
    current?.value !== null
      ? Number(current.value)
      : null;

  const previousValue =
    previous?.value !== undefined &&
    previous?.value !== null
      ? Number(previous.value)
      : null;

  const chartItem =
    payload[0]?.payload;

  const displayDate =
    chartItem?.dateLabel ||
    label ||
    '';

  return (
    <div
      className="
        min-w-[165px]
        overflow-hidden
        rounded-xl
        border
        border-[#E5E7EB]
        bg-white
        shadow-xl
      "
    >
      {/* Header */}

      <div
        className="
          border-b
          border-[#EEF2F7]
          bg-[#F8FAFC]
          px-3
          py-2
        "
      >
        <span
          className="
            text-[12px]
            font-semibold
            text-[#0F172A]
          "
        >
          {displayDate}
        </span>
      </div>

      {/* Body */}

      <div
        className="
          space-y-2
          px-3
          py-2.5
        "
      >
        {/* This Week */}

        {currentValue !== null && (
          <div
            className="
              flex
              items-center
              justify-between
              gap-4
            "
          >
            <div
              className="
                flex
                items-center
                gap-2
              "
            >
              <span
                className="
                  h-2.5
                  w-2.5
                  shrink-0
                  rounded-full
                "
                style={{
                  backgroundColor:
                    CURRENT_COLOR,
                }}
              />

              <span
                className="
                  text-[11px]
                  text-[#334155]
                "
              >
                This Week
              </span>
            </div>

            <span
              className="
                text-[11px]
                font-semibold
                text-[#284F79]
              "
            >
              {currentValue.toFixed(2)}%
            </span>
          </div>
        )}

        {/* Last Week */}

        {previousValue !== null && (
          <div
            className="
              flex
              items-center
              justify-between
              gap-4
            "
          >
            <div
              className="
                flex
                items-center
                gap-2
              "
            >
              <span
                className="
                  h-2.5
                  w-2.5
                  shrink-0
                  rounded-full
                "
                style={{
                  backgroundColor:
                    PREVIOUS_COLOR,
                }}
              />

              <span
                className="
                  text-[11px]
                  text-[#64748B]
                "
              >
                Last Week
              </span>
            </div>

            <span
              className="
                text-[11px]
                font-semibold
                text-[#64748B]
              "
            >
              {previousValue.toFixed(2)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================================================
// DYNAMIC Y AXIS
// ==========================================================================

const getNiceYAxisMax = (
  maxValue: number,
): number => {
  if (
    !Number.isFinite(maxValue) ||
    maxValue <= 0
  ) {
    return 10;
  }

  const paddedMax =
    maxValue * 1.1;

  if (paddedMax <= 10) {
    return Math.ceil(
      paddedMax,
    );
  }

  if (paddedMax <= 20) {
    return (
      Math.ceil(
        paddedMax / 2,
      ) * 2
    );
  }

  if (paddedMax <= 50) {
    return (
      Math.ceil(
        paddedMax / 5,
      ) * 5
    );
  }

  if (paddedMax <= 100) {
    return (
      Math.ceil(
        paddedMax / 10,
      ) * 10
    );
  }

  return (
    Math.ceil(
      paddedMax / 20,
    ) * 20
  );
};

// ==========================================================================

const getYAxisTicks = (
  maxValue: number,
): number[] => {
  if (maxValue <= 1) {
    return [0, 1];
  }

  if (maxValue <= 10) {
    const ticks: number[] = [];

    for (
      let value = 0;
      value <= maxValue;
      value += 2
    ) {
      ticks.push(value);
    }

    if (
      ticks[ticks.length - 1] !==
      maxValue
    ) {
      ticks.push(maxValue);
    }

    return ticks;
  }

  let step = 5;

  if (maxValue > 50) {
    step = 10;
  }

  if (maxValue > 100) {
    step = 20;
  }

  const ticks: number[] = [];

  for (
    let value = 0;
    value <= maxValue;
    value += step
  ) {
    ticks.push(value);
  }

  if (
    ticks[ticks.length - 1] !==
    maxValue
  ) {
    ticks.push(maxValue);
  }

  return ticks;
};

// ==========================================================================
// X AXIS LABEL
// ==========================================================================

const getXAxisLabel = (
  item: ChartDataItem,
): string => {
  return getShortDateLabel(
    item.dayName,
    item.workDate,
  );
};

// ==========================================================================
// COMPONENT
// ==========================================================================

const WtdTrendChart = ({
  data = [],
}: Props) => {
  // ------------------------------------------------------------------------
  // CHART REF
  // ------------------------------------------------------------------------

  const chartWheelRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  // ------------------------------------------------------------------------
  // ACTIVE LINE
  // ------------------------------------------------------------------------

  const [
    activeLine,
    setActiveLine,
  ] = useState<
    'current' | 'previous' | null
  >(null);

  // ------------------------------------------------------------------------
  // ZOOM
  // ------------------------------------------------------------------------

  const [
    startIndex,
    setStartIndex,
  ] = useState(0);

  const [
    endIndex,
    setEndIndex,
  ] = useState(
    Math.max(
      0,
      data.length - 1,
    ),
  );

  // ------------------------------------------------------------------------
  // FORMAT API DATA
  // ------------------------------------------------------------------------

  const chartData =
    useMemo<ChartDataItem[]>(
      () => {
        if (
          !Array.isArray(data)
        ) {
          return [];
        }

        return data
          .map(
            (item, index) => {
              const workDate =
                String(
                  item.workDate ||
                    '',
                ).trim();

              const dayName =
                String(
                  item.dayName ||
                    '',
                ).trim();

              return {
                /*
                 * IMPORTANT:
                 * Every point has a unique key.
                 */
                axisKey:
                  `${workDate}-${index}`,

                workDate,

                dayName,

                day:
                  getShortDateLabel(
                    dayName,
                    workDate,
                  ),

                dateLabel:
                  getLongDateLabel(
                    workDate,
                  ),

                current:
                  Number(
                    item.currentProductivity ??
                      0,
                  ),

                previous:
                  Number(
                    item.previousProductivity ??
                      0,
                  ),
              };
            },
          )
          .filter(
            (item) =>
              Boolean(
                item.workDate,
              ),
          );
      },
      [data],
    );

  // ------------------------------------------------------------------------
  // Y AXIS
  // ------------------------------------------------------------------------

  const yAxisMax =
    useMemo(() => {
      if (
        chartData.length === 0
      ) {
        return 10;
      }

      const values =
        chartData.flatMap(
          (item) => [
            item.current,
            item.previous,
          ],
        );

      const validValues =
        values.filter(
          (value) =>
            Number.isFinite(
              value,
            ),
        );

      if (
        validValues.length === 0
      ) {
        return 10;
      }

      const maxValue =
        Math.max(
          ...validValues,
        );

      return getNiceYAxisMax(
        maxValue,
      );
    }, [chartData]);

  const yAxisTicks =
    useMemo(
      () =>
        getYAxisTicks(
          yAxisMax,
        ),
      [yAxisMax],
    );

  // ------------------------------------------------------------------------
  // RESET ZOOM WHEN DATA CHANGES
  // ------------------------------------------------------------------------

  useEffect(() => {
    setStartIndex(0);

    setEndIndex(
      Math.max(
        0,
        chartData.length - 1,
      ),
    );
  }, [chartData.length]);

  // ------------------------------------------------------------------------
  // VISIBLE DATA
  // ------------------------------------------------------------------------

  const visibleData =
    useMemo(() => {
      if (
        chartData.length === 0
      ) {
        return [];
      }

      const safeStart =
        Math.max(
          0,
          Math.min(
            startIndex,
            chartData.length - 1,
          ),
        );

      const safeEnd =
        Math.max(
          safeStart,
          Math.min(
            endIndex,
            chartData.length - 1,
          ),
        );

      return chartData.slice(
        safeStart,
        safeEnd + 1,
      );
    }, [
      chartData,
      startIndex,
      endIndex,
    ]);

  // ------------------------------------------------------------------------
  // X AXIS TICKS
  //
  // ALL visible points are shown.
  // No 7-label limitation.
  // ------------------------------------------------------------------------

  const xAxisTicks =
    useMemo(
      () =>
        visibleData.map(
          (item) =>
            item.axisKey,
        ),
      [visibleData],
    );

  // ------------------------------------------------------------------------
  // CHART WHEEL ZOOM
  // ------------------------------------------------------------------------

  useEffect(() => {
    const element =
      chartWheelRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      event.preventDefault();
      event.stopPropagation();

      if (
        chartData.length <=
        MIN_VISIBLE_POINTS
      ) {
        return;
      }

      // ZOOM IN

      if (event.deltaY < 0) {
        setStartIndex(
          (previousStart) => {
            const maximumStart =
              Math.max(
                0,
                chartData.length -
                  MIN_VISIBLE_POINTS,
              );

            return Math.min(
              previousStart + 1,
              maximumStart,
            );
          },
        );

        setEndIndex(
          (previousEnd) => {
            const minimumEnd =
              Math.min(
                MIN_VISIBLE_POINTS - 1,
                chartData.length - 1,
              );

            return Math.max(
              previousEnd - 1,
              minimumEnd,
            );
          },
        );

        return;
      }

      // ZOOM OUT

      if (event.deltaY > 0) {
        setStartIndex(
          (previousStart) =>
            Math.max(
              0,
              previousStart - 1,
            ),
        );

        setEndIndex(
          (previousEnd) =>
            Math.min(
              chartData.length - 1,
              previousEnd + 1,
            ),
        );
      }
    };

    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, [
    chartData.length,
  ]);

  // ------------------------------------------------------------------------
  // EMPTY STATE
  // ------------------------------------------------------------------------

  if (!chartData.length) {
    return (
      <div
        className="
          h-full
          min-h-[330px]
          w-full
          rounded-2xl
          border
          border-[#D9DDE3]
          bg-white
          p-5
        "
      >
        <div
          className="
            mb-4
            flex
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              text-[14px]
              font-semibold
              text-[#003E7E]
            "
          >
            WTD Trend
          </h3>

          <span
            className="
              rounded-full
              bg-[#EAF3FF]
              px-3
              py-1
              text-[10px]
              text-[#2563EB]
            "
          >
            Span avg. productive time ·
            Mon–Fri
          </span>
        </div>

        <div
          className="
            flex
            h-[250px]
            items-center
            justify-center
            text-[12px]
            text-[#667085]
          "
        >
          No data available
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------------

  return (
    <div
      className="
        h-full
        min-h-[330px]
        w-full
        min-w-0
        overflow-hidden
        rounded-2xl
        border
        border-[#D9DDE3]
        bg-white
        p-5
      "
    >
      {/* ================================================================
          HEADER
      ================================================================ */}

      <div
        className="
          mb-3
          flex
          flex-wrap
          items-center
          justify-between
          gap-2
        "
      >
        <div
          className="
            flex
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              text-[14px]
              font-semibold
              text-[#003E7E]
            "
          >
            WTD Trend
          </h3>

          <span
            className="
              rounded-full
              bg-[#EAF3FF]
              px-3
              py-1
              text-[10px]
              text-[#2563EB]
            "
          >
            Span avg. productive time ·
            Mon–Fri
          </span>
        </div>

        <span
          className="
            rounded-full
            bg-[#F8FAFC]
            px-2.5
            py-1
            text-[9px]
            text-[#64748B]
          "
        >
          {visibleData.length}
          {' / '}
          {chartData.length}
          {' days'}
        </span>
      </div>

      {/* ================================================================
          LEGEND
      ================================================================ */}

      <div
        className="
          mb-4
          flex
          flex-wrap
          items-center
          justify-center
          gap-6
          text-[11px]
          font-medium
        "
      >
        {/* This Week */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-1.5
          "
          onMouseEnter={() =>
            setActiveLine(
              'current',
            )
          }
          onMouseLeave={() =>
            setActiveLine(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
            "
            style={{
              backgroundColor:
                CURRENT_COLOR,
              opacity:
                activeLine === null ||
                activeLine ===
                  'current'
                  ? 1
                  : 0.3,
            }}
          />

          <span
            style={{
              color:
                CURRENT_COLOR,
              fontWeight:
                activeLine ===
                'current'
                  ? 600
                  : 400,
              opacity:
                activeLine === null ||
                activeLine ===
                  'current'
                  ? 1
                  : 0.4,
            }}
          >
            This Week
          </span>
        </div>

        {/* Last Week */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-1.5
          "
          onMouseEnter={() =>
            setActiveLine(
              'previous',
            )
          }
          onMouseLeave={() =>
            setActiveLine(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
            "
            style={{
              backgroundColor:
                PREVIOUS_COLOR,
              opacity:
                activeLine === null ||
                activeLine ===
                  'previous'
                  ? 1
                  : 0.3,
            }}
          />

          <span
            style={{
              color:
                PREVIOUS_COLOR,
              fontWeight:
                activeLine ===
                'previous'
                  ? 600
                  : 400,
              opacity:
                activeLine === null ||
                activeLine ===
                  'previous'
                  ? 1
                  : 0.4,
            }}
          >
            Last Week
          </span>
        </div>
      </div>

      {/* ================================================================
          CHART
      ================================================================ */}

      <div
        ref={chartWheelRef}
        className="
          relative
          h-[275px]
          w-full
          overflow-hidden
          rounded-[10px]
        "
        style={{
          overscrollBehavior:
            'contain',
          touchAction:
            'pan-y',
        }}
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <LineChart
            data={visibleData}
            margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: 55,
            }}
          >
            {/* ==========================================================
                GRID
            ========================================================== */}

            <CartesianGrid
              stroke={GRID_COLOR}
              strokeDasharray="0"
              vertical={false}
            />

            {/* ==========================================================
                X AXIS

                ALL dates are displayed.

                Labels are rotated -35 degrees.
            ========================================================== */}

            <XAxis
              dataKey="axisKey"

              /*
               * Every visible point.
               */
              ticks={xAxisTicks}

              /*
               * Do not allow Recharts
               * to hide ticks.
               */
              interval={0}

              axisLine={{
                stroke:
                  '#D0D5DD',
              }}

              tickLine={false}

              tickMargin={10}

              /*
               * Slightly diagonal.
               *
               * This is the main visual
               * change you requested.
               */
              angle={-35}

              /*
               * Rotated text should end
               * at the point.
               */
              textAnchor="end"

              /*
               * More room for rotated labels.
               */
              height={65}

              tickFormatter={(
                value,
              ) => {
                const item =
                  visibleData.find(
                    (
                      chartItem,
                    ) =>
                      chartItem.axisKey ===
                      value,
                  );

                if (!item) {
                  return '';
                }

                return getXAxisLabel(
                  item,
                );
              }}

              tick={{
                fontSize: 9,
                fill: AXIS_COLOR,
                fontWeight: 500,
              }}

              padding={{
                left: 8,
                right: 8,
              }}
            />

            {/* ==========================================================
                Y AXIS
            ========================================================== */}

            <YAxis
              domain={[
                0,
                yAxisMax,
              ]}
              ticks={
                yAxisTicks
              }
              allowDecimals={false}
              tickFormatter={(
                value,
              ) =>
                `${value}%`
              }
              axisLine={false}
              tickLine={false}
              width={42}
              tick={{
                fontSize: 11,
                fill: AXIS_COLOR,
              }}
            />

            {/* ==========================================================
                TOOLTIP
            ========================================================== */}

            <Tooltip
              content={
                <CustomTooltip />
              }
              cursor={{
                stroke:
                  '#CBD5E1',
                strokeWidth: 1.5,
                strokeDasharray:
                  '4 4',
              }}
              wrapperStyle={{
                outline: 'none',
                zIndex: 1000,
              }}
            />

            {/* ==========================================================
                THIS WEEK
            ========================================================== */}

            <Line
              type="monotone"
              dataKey="current"
              name="This Week"
              stroke={
                CURRENT_COLOR
              }
              strokeWidth={
                activeLine ===
                'current'
                  ? 4
                  : 3
              }
              strokeOpacity={
                activeLine &&
                activeLine !==
                  'current'
                  ? 0.2
                  : 1
              }
              dot={{
                r:
                  activeLine ===
                  'current'
                    ? 4
                    : 3,
                fill:
                  CURRENT_COLOR,
                stroke:
                  CURRENT_COLOR,
              }}
              activeDot={{
                r: 6,
              }}
              onMouseEnter={() =>
                setActiveLine(
                  'current',
                )
              }
              onMouseLeave={() =>
                setActiveLine(null)
              }
              isAnimationActive={
                false
              }
            />

            {/* ==========================================================
                LAST WEEK
            ========================================================== */}

            <Line
              type="monotone"
              dataKey="previous"
              name="Last Week"
              stroke={
                PREVIOUS_COLOR
              }
              strokeWidth={
                activeLine ===
                'previous'
                  ? 3
                  : 2
              }
              strokeDasharray="5 5"
              strokeOpacity={
                activeLine &&
                activeLine !==
                  'previous'
                  ? 0.2
                  : 1
              }
              dot={{
                r: 3,
                fill:
                  PREVIOUS_COLOR,
                stroke:
                  PREVIOUS_COLOR,
              }}
              activeDot={{
                r: 5,
              }}
              onMouseEnter={() =>
                setActiveLine(
                  'previous',
                )
              }
              onMouseLeave={() =>
                setActiveLine(null)
              }
              isAnimationActive={
                false
              }
            />
          </LineChart>
        </ResponsiveContainer>

        {/* ==============================================================
            Y AXIS INFO
        ============================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            left-[46px]
            top-1
            rounded-full
            bg-white/90
            px-2
            py-1
            text-[9px]
            text-[#667085]
            shadow-sm
          "
        >
          Max {yAxisMax}%
        </div>
      </div>
    </div>
  );
};

export default WtdTrendChart;